import React, { useState } from "react";
import { faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import Loader from "../../Layout/Loader";
import PreviewEmployeeReferenceData from "./PreviewEmployeeReferenceData";
import * as xlsx from "xlsx";
import "./importEmployees.scss";

const ImportEmployees = ({ visible, onHide, onImport }) => {
  const { t } = useTranslation("translations");
  const [state, setState] = useState({
    file: null,
    name: null,
    extension: "",
  });
  const [previewVisible, setPreviewVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleFile = (e) => {
    const file = e.target.files[0];
    const extension =
      file && file.name.split(".")[file.name.split(".").length - 1];
    const maxFileSize = 5 * 1024 * 1024; // 5MB in bytes

    if (file && file.size > maxFileSize) {
      toast.error(t("notification.fileSizeExceeded") || "File size exceeds 5MB limit", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        progress: undefined,
        containerId: "all_toast",
      });
      return;
    }

    if (extension && extension === "csv") {
      const reader = new FileReader();

      reader.onload = (readerEvent) => {
        let binaryString = readerEvent.target.result;
        setState((state) => ({
          ...state,
          file: btoa(binaryString),
          name: file.name,
          extension,
        }));
      };
      reader.readAsBinaryString(file);
    }
  };

  const submitEmployees = (e) => {
    e.preventDefault();
    if (!state.file) {
      toast.error(t("notification.pleaseSelectFile") || "Please select a file", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        progress: undefined,
        containerId: "all_toast",
      });
      return;
    }

    setLoading(true);

    try {
      // Decode the base64 file
      const binaryString = atob(state.file);

      // Parse CSV
      const workbook = xlsx.read(binaryString, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const data = xlsx.utils.sheet_to_json(worksheet);

      if (!data || data.length === 0) {
        toast.error(t("notification.emptyFile") || "The file is empty", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          progress: undefined,
          containerId: "all_toast",
        });
        setLoading(false);
        return;
      }

      // Map CSV data to employee format
      const employees = data.map((row) => ({
        employeeFullName: row.name || row.employeeName || "",
        connectionAccount: {
          financialInstitution: row.bic ? { bic: row.bic } : null,
        },
        iban: row.iban || "",
        amount: row.amount ? parseFloat(row.amount) : 0,
        currency: row.currency || "ALL",
        description: row.description || "",
      }));

      // Call the onImport callback to pass the data to parent
      onImport(employees);

      // Reset state and close dialog
      setState({
        file: null,
        name: null,
        extension: "",
      });
      onHide();
    } catch (error) {
      console.error("Error importing employees:", error);
      toast.error(t("notification.error") || "Error importing employees", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        progress: undefined,
        containerId: "all_toast",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        header={t("payRoll.importEmployees") || "Import Employees"}
        visible={visible}
        onHide={onHide}
        style={{ width: "26vw" }}
        breakpoints={{ "960px": "75vw", "641px": "100vw" }}
      >
        <form onSubmit={submitEmployees}>
          <div className="form-group">
            <div
              style={{
                backgroundColor: "#fffbea",
                border: "1px solid #ffe58f",
                borderRadius: "4px",
                padding: "0.75rem 1rem",
                marginBottom: "1rem",
              }}
            >
              <p
                style={{
                  marginBottom: "0.2rem",
                  color: "#664d03",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                }}
              >
                {t("common.formatdocument")} (CSV)
              </p>
              <p style={{ marginBottom: "0", color: "#664d03", fontSize: "0.85rem" }}>
                Max file size: 5mb
              </p>
            </div>
            <label htmlFor="employeeFile" style={{ fontWeight: "500", marginBottom: "0.5rem" }}>
              {t("invoice.file")}
            </label>
            <input
              type="file"
              className="form-control"
              id="file"
              name="file"
              placeholder="Upload File"
              accept=".csv"
              onClick={(e) => {
                e.target.value = null;
              }}
              onChange={handleFile}
            />
            {state.name && <small className="text-muted mt-1 d-block">{state.name}</small>}
          </div>

          <div className="import-templates-section mt-4">
            <p className="import-section-title">
              <strong>{t("exports.templates")}</strong>
            </p>

            <div className="templates-grid">
              <a
                href="/_employee_csv_template.csv"
                className="btn btn-sm btn-outline-primary"
                download="/_employee_csv_template.csv"
              >
                <i className="pi pi-download mr-2"></i>
                {t("exports.importTemplate")}
              </a>

              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => setPreviewVisible(true)}
                title="Preview Reference Data"
              >
                <i className="pi pi-eye mr-2"></i>
                {t("exports.referenceData")}
              </button>
            </div>
          </div>

          <button
            className="btn btn-primary mt-3"
            disabled={loading || !state.file}
            style={{ minWidth: "80px" }}
            type="submit"
          >
            {loading ? (
              <Loader size="xs" color="white" />
            ) : (
              <>
                <FontAwesomeIcon icon={faUpload} className="mr-2" />
                {t("common.import")}
              </>
            )}
          </button>
        </form>
      </Dialog>

      <PreviewEmployeeReferenceData
        visible={previewVisible}
        onHide={() => setPreviewVisible(false)}
      />
    </>
  );
};

export default ImportEmployees;
